import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { isEmpty } from "lodash";

import { InstructorOtherComments, InstructorOtherCommentsReg } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { UtilityService } from "src/app/util/utility.service";
import { ContainerRatingsService } from "../../services/base/container-ratings.services";

@Component({
	selector: "app-other-training-comments",
	templateUrl: "./other-training-comments.component.html",
	styleUrls: ["./other-training-comments.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class OtherTrainingCommentsComponent implements OnInit {
	maneuverForm!: UntypedFormGroup;
	parentForm!: UntypedFormArray;
	parentFormArrayIndex: number = 0;

	@Input() tabIndex!: number;
	@Input() commentAiAssist = false;
	/** TS1 v3: mirror maneuver comment to all PF/PM observation forms. */
	@Input() onManeuverCommentSaved?: (maneuverCode: string, comment: string) => void;
	@Input("maneuverForm") set _(value: any) {
		this.maneuverForm = value as UntypedFormGroup;
	}

	otherTrainingComments: InstructorOtherComments[] = [];

	constructor(
		public sharedService: SharedService,
		private httpService: HttpService,
		private loadingService: LoaderService,
		private cd: ChangeDetectorRef,
		private dialog: MatDialog,
		private util: UtilityService,
		private containerRatingsService: ContainerRatingsService,
	) {}

	ngOnInit(): void {
		this.parentForm = this.maneuverForm.parent as UntypedFormArray;
		this.parentFormArrayIndex = this.parentForm.controls.findIndex((subform) => subform == this.maneuverForm);

		this.maneuverForm?.valueChanges.subscribe((value: any) => {
			const options = this.sharedService.dropDownOptionsManeuver;

			if (!isEmpty(value)) {
				options.find((m) => m.code === value.maneuverCode); // keep lookup as before

				this.otherTrainingComments = [];
				this.otherTrainingComments.push({
					candidateId: value.candidateId,
					maneuver: "",
					maneuverCode: value.maneuverCode,
					comment: value.otherComents,
					createdAt: new Date().toISOString(),
				});
			}
		});
	}

	// ===== service-backed flags =====
	get inDraftMode(): boolean {
		return this.containerRatingsService.inDraftMode;
	}
	get isSynched(): boolean {
		return this.containerRatingsService.isSynced;
	}

	// ===== getters =====
	get formIndex() {
		return this.parentForm.controls.findIndex((subform) => subform == this.maneuverForm);
	}
	get maneuver() {
		return this.maneuverForm.get("maneuver")!;
	}
	get maneuverCode() {
		return this.maneuverForm.get("maneuverCode")!;
	}
	get assessmentId() {
		return this.maneuverForm.get("assessmentId")!;
	}
	get traineeAssessmentId() {
		return this.maneuverForm.get("traineeAssessmentId")!;
	}
	get candidateId() {
		return this.maneuverForm.get("candidateId")!;
	}
	get otherComents() {
		return this.maneuverForm.get("otherComents")!;
	}

	// ===== UI =====
	viewOtherSummaryModal() {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "view-summary-modal",
			height: "560px",
			width: "1154px",
			hasBackdrop: true,
			data: {
				modalTitle: "Other Training & Observations",
				modalName: "viewOtherSummaryModal",
				modalContent: this.otherTrainingComments,
				modalTraineeAssessmentId: this.traineeAssessmentId.value,
				noCloseBtn: true,
				modalBtnConfirmText: "Close",
			},
		});

		const sub = dialogRef.afterClosed().subscribe(() => sub.unsubscribe());
	}

	onOtherTrainingCommentsAdded() {
		this.otherCommentsModal();
	}

	otherCommentsModal() {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "other-comments-modal",
			height: "560px",
			width: "1140px",
			hasBackdrop: true,
			data: {
				modalName: "OtherTrainingCommentsModal",
				modalTitle: "Other Training & Observations",
				commentAiAssist: this.commentAiAssist,
				modalBtnConfirmText: "Save",
				modalBtnCancelText: "Cancel",
				disableClose: true,
				maneuverOtherComments: {
					otherComent: this.otherComents.value,
					maneuver: this.maneuver.value,
				},
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: { close: boolean; otherComments: string }) => {
			if ((result?.otherComments || result?.otherComments === "") && result.close === true) {
				// 1) Keep *exact* data you’ve been saving, WITH createdAt (TS-safe)
				const reg: InstructorOtherCommentsReg = {
					candidateId: this.candidateId.value,
					maneuverCode: this.maneuverCode.value,
					comment: result.otherComments,
					createdAt: new Date().toISOString(),
				};
				const dataToStore = { ...reg, createdAt: new Date().toISOString() } as any;

				// 2) UI row (unchanged)
				const uidata: InstructorOtherComments = {
					candidateId: this.candidateId.value,
					maneuver: this.maneuver.value,
					maneuverCode: this.maneuverCode.value,
					comment: result.otherComments,
					createdAt: new Date().toISOString(),
				};

				// 3) reflect in control
				this.otherComents.patchValue(result.otherComments);
				this.onManeuverCommentSaved?.(this.maneuverCode.value, result.otherComments);
				this.cd.detectChanges();

				// 4) Update draft via ContainerRatingsService (no direct DB calls)
				const record = this.containerRatingsService.draft;
				const cr = (record?.candidateRating ?? {}) as any;
				const otherComments = [
					...(cr.otherComments ?? []).filter(
						(entry: { maneuverCode?: string }) => entry.maneuverCode !== this.maneuverCode.value,
					),
					dataToStore,
				];

				console.log("final other comments", otherComments);

				this.containerRatingsService.updateSelectedDraft({
					candidateRating: {
						...cr,
						otherComments,
					},
					continuePage: 2,
				});

				// 5) Queue sync event (same event name)
				// this.containerRatingsService.addToBeSynched("maneuver_comments", dataToStore);

				// 6) Refresh local list for modal preview
				this.otherTrainingComments = [uidata];
			}
			sub.unsubscribe();
		});
	}
}
